import React from "react";
import { Link } from "react-router-dom";
import { ReactComponent as BrandMark } from "../../../assets/svg/Dsahboard/brand-stepup-icon.svg";
import { ReactComponent as PlansMark } from "../../../assets/svg/Dsahboard/plans-icon.svg";
import { ReactComponent as AddClientMark } from "../../../assets/svg/Dsahboard/add-client-icon.svg";
import type { SetupStep, SetupStepId } from "../setupSteps";

/** The mark at the head of a step — the same artwork the wide screen's
 *  `SetupChecklist` uses, discs and all, only smaller. Each file carries its
 *  own circle and its own tint, so there is nothing to colour here. */
const STEP_MARKS: Record<SetupStepId, React.ElementType> = {
  brand: BrandMark,
  plans: PlansMark,
  client: AddClientMark,
};

/** The green a finished step is ticked in. */
const DONE_GREEN = "#00A33F";

/**
 * What a step offers, as the row writes it.
 *
 * The list's notes carry a `+` so the wide screen's steps read as offers
 * stacked under their titles — see `setupSteps`. Here the offer is a word at
 * the end of the row, where a `+` would read as a second control beside it.
 */
const offer = (note: string) => note.replace(/^\+\s*/, "");

/** The tick on a step behind them: a filled disc rather than the ring the rest
 *  of the app uses, because it is the one thing on this row that has to carry
 *  at a glance down a column of three. */
const DoneTick: React.FC = () => (
  <span
    aria-hidden
    className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full"
    style={{ backgroundColor: DONE_GREEN }}
  >
    {/* The svg spans the disc and the tick is drawn across the middle of it,
        so the stroke's width is set here rather than left to how far a small
        box is scaled up: a tick a third of the disc wide read as a dot. */}
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="#FFFFFF"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-7 w-7"
    >
      <path d="M6.5 12.25L10 15.75L17.5 8.25" />
    </svg>
  </span>
);

interface MobileSetupCardProps {
  steps: SetupStep[];
}

/**
 * What is left to set up, as a phone reads it.
 *
 * The wide screen draws the same three steps as a journey across the welcome
 * card: marks on one line, legs between them, the note under each title and a
 * halo under the one to press. None of that survives a 360px column. Laid out
 * side by side the titles wrap to two lines and the legs join thirds of a card
 * rather than steps; so on a phone the three stack, which is the shape every
 * other list on this app's phone screens takes.
 *
 * What the journey said is said by the rows instead. How far along they are is
 * a count in the heading rather than a line of travelled legs — "2 of 3 done"
 * is the same fact in the space a phone has for it. Which step is theirs is the
 * one row with something to press at the end of it, so the halo has nothing
 * left to point at and is not drawn: a Lottie loop behind a 40px disc would be
 * decoration, not direction.
 *
 * The whole row is the link where there is one, as on the wide screen — the
 * offer at the end is where the eye lands rather than a second target.
 */
const MobileSetupCard: React.FC<MobileSetupCardProps> = ({ steps }) => {
  const done = steps.filter((step) => step.state === "done").length;

  return (
    <section className="card-surface border border-line bg-white px-4 py-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-[16px] font-semibold leading-tight text-ink">
          Finish setting up
        </h2>
        {/* How far along, in the room the journey's legs used to take. */}
        <span className="flex-shrink-0 rounded-full bg-brand-soft px-3 py-1 text-[12px] font-medium text-brand-dark">
          {done} of {steps.length} done
        </span>
      </div>

      <ul className="mt-1">
        {steps.map((step, index) => {
          const Mark = STEP_MARKS[step.id];
          const to = step.to;

          const body = (
            <>
              <Mark aria-hidden className="h-10 w-10 flex-shrink-0" />
              {/* Wrapping rather than truncating: a title clipped to
                  "Add your first…" says less than one on two lines, and at
                  this width that is the usual case rather than the edge one. */}
              <span className="min-w-0 flex-1 text-[15px] font-medium leading-snug text-ink">
                {step.title}
              </span>
              {step.state === "done" ? (
                <DoneTick />
              ) : (
                // The offer where the step can be pressed, and the standing
                // note where it cannot — the same two things the wide screen
                // writes under the title, told apart by colour as well as by
                // words. See `setupSteps`.
                <span
                  className={`flex-shrink-0 text-[14px] font-semibold ${
                    to ? "text-brand-dark" : "text-ink-subtle font-medium"
                  }`}
                >
                  {offer(step.note)}
                </span>
              )}
            </>
          );

          return (
            <li
              key={step.id}
              className={index > 0 ? "border-t border-line" : undefined}
            >
              {to ? (
                <Link
                  to={to}
                  className="-mx-2 flex items-center gap-3 rounded-md px-2 py-3.5 transition-colors hover:bg-[#F7FBFC] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                >
                  {body}
                </Link>
              ) : (
                <div className="flex items-center gap-3 py-3.5">{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default MobileSetupCard;
